import { IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight, SquareArrowLeft } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ConfirmationModal } from "src/components/ConfirmationModal";
import { MultiStepFormStep } from "src/components/FormStep/MultiStepFormStep";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import { DocumentAnswer } from "src/stores/models/document";
import { attachActionsToQuestions } from "src/utils/documentTemplates";
import {
  MultiStepFormErrorBoundary,
  MultiStepFormSkeleton,
  PageHeader,
  ProgressBubbles,
  UpdateDeviceDescriptionModal,
} from "../components";
import {
  useCreateDocumentVersionMutation,
  useDocEngine,
  useGetDevice,
  useGetDocuments,
  useGetDocumentsTemplates,
  useGetDocumentWithAllVersions,
  useGetTasks,
  useGetUser,
} from "../hooks";
import { DocStatus, DocumentStatus, TemplateQuestion } from "../types";
import {
  fillRouteWithSlugs,
  getDocVersionStatus,
  getFilteredElements,
} from "../utils";

const returnToAssistantText = "Assistant";
const returnToRoadmapText = "Roadmap";
const returnToQmsText = "Documents";

export const MultiStepForm: React.FC = () => {
  const {
    orgId = "",
    deviceId = "",
    docId = "",
  } = useParams<{
    orgId: string;
    deviceId: string;
    docId?: string;
  }>();

  const navigate = useNavigate();
  const { state } = useLocation();
  const [previousPath, setPreviousPath] = useState<string>("");
  const [previousPathButtonText, setPreviousPathButtonText] =
    useState<string>("");

  const [_, setSearchParams] = useSearchParams();
  const docEngine = useDocEngine(deviceId);
  const [statusGuardModal, setStatusGuardModal] = useState<Extract<
    DocStatus,
    "UNAPPROVED" | "LIVE"
  > | null>(null);
  const createVersionMutation = useCreateDocumentVersionMutation(
    orgId,
    deviceId
  );

  if (!deviceId) {
    throw new Error("No device id specified");
  }

  const containerRef = React.useRef<HTMLDivElement>(null);
  const hasCheckedStatus = useRef(false);

  let [searchParams] = useSearchParams();
  const versionIdParam = searchParams.get("v");

  /**
   * QUERIES
   */
  const { data: user } = useGetUser();
  const { data: device } = useGetDevice({ orgId, deviceId });
  const { data: document } = useGetDocumentWithAllVersions(orgId, docId);
  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId,
  });
  const { data: documents } = useGetDocuments(orgId, deviceId);

  const { data: tasks } = useGetTasks(orgId, deviceId);

  const steps = useMemo(
    () =>
      document?.versions[0]
        ? attachActionsToQuestions(
            document.versions[0].dataKeys,
            templatesConfig
          )
        : [],
    [document?.versions[0]?.dataKeys, templatesConfig]
  );

  /**
   * This hook gets the filtered steps and sets the first step as the active step if no step is set in the url
   */
  useEffect(() => {
    if (device && documents && steps.length > 0) {
      const filteredSteps = getFilteredElements(steps, device, documents);

      let stepIdUrlParam = searchParams.get("step");

      if (!stepIdUrlParam && filteredSteps[0]) {
        const nextStepId = filteredSteps[0].id;
        if (nextStepId) {
          setSearchParams((params) => {
            const nextParams = new URLSearchParams(params);
            nextParams.set("step", nextStepId);
            return nextParams;
          });
        }
      }
    }
  }, [device?.id, searchParams, documents, steps]);

  // Set the exit path for the top bar, from the previous page when we first get to this component
  useEffect(() => {
    // Save the previous path on first render
    if (!previousPath && state?.from) {
      const getNavigationState = () => {
        const isQmsNavigation = state.from.includes("/documents");
        const isAssistantNavigation = state.from.includes("/assistant");

        if (isQmsNavigation) {
          return {
            path: state.from,
            text: returnToQmsText,
          };
        }

        if (isAssistantNavigation) {
          return {
            path: state.from,
            text: returnToAssistantText,
          };
        }

        return {
          path: state.from,
          text: returnToRoadmapText,
        };
      };

      const { path, text } = getNavigationState();
      setPreviousPath(path);
      setPreviousPathButtonText(text);
    }
  }, [state?.from, previousPath, docId]);

  // Safety net: show a modal if the version isn't DRAFT
  // (see DOCUMENT_FLOW.md § Template Assistant / Invariant 7)
  useEffect(() => {
    if (!document || !user) return;
    if (hasCheckedStatus.current) return;
    hasCheckedStatus.current = true;

    const version = document.versions[0];
    if (!version) return;
    const status = getDocVersionStatus({
      selectedDocVersion: version,
      document,
      user,
      orgId,
    });
    if (status === "UNAPPROVED") {
      setStatusGuardModal(DocumentStatus.UNAPPROVED);
    } else if (status === "LIVE") {
      setStatusGuardModal(DocumentStatus.LIVE);
    }
  }, [document, user, orgId]);

  const getLeftButtonProps = () => {
    if (previousPath && previousPathButtonText) {
      return {
        onClick: () => navigate(previousPath),
        text: previousPathButtonText,
      };
    }
    return {
      onClick: () =>
        navigate(
          fillRouteWithSlugs(ROUTES.DEVICE_OVERVIEW, {
            [ROUTE_SLUGS.ORG_ID]: orgId,
            [ROUTE_SLUGS.DEVICE_ID]: deviceId,
          })
        ),
      text: returnToRoadmapText,
    };
  };

  const handleNextStep = (
    step: TemplateQuestion,
    steps: TemplateQuestion[]
  ) => {
    const nextStep = steps[steps.findIndex((s) => s.id === step.id) + 1];
    setSearchParams((params) => {
      const nextParams = new URLSearchParams(params);
      nextParams.set("step", nextStep.id);
      return nextParams;
    });
  };

  const handlePrevStep = (
    step: TemplateQuestion,
    steps: TemplateQuestion[]
  ) => {
    const prevStep = steps[steps.findIndex((s) => s.id === step.id) - 1];
    setSearchParams((params) => {
      const nextParams = new URLSearchParams(params);
      nextParams.set("step", prevStep.id);
      return nextParams;
    });
  };

  if (
    docEngine.pending ||
    !device ||
    !document ||
    !documents ||
    !document.versions[0] ||
    !user ||
    !tasks ||
    !templatesConfig ||
    (versionIdParam !== null && document.versions[0].id !== versionIdParam)
  ) {
    return <MultiStepFormSkeleton />;
  }

  const documentVersion = document.versions[0];

  const handleNavigateBack = () => {
    if (previousPath) {
      navigate(previousPath);
    } else {
      navigate(
        fillRouteWithSlugs(ROUTES.QMS_OPEN_DOC, {
          [ROUTE_SLUGS.ORG_ID]: orgId,
          [ROUTE_SLUGS.DEVICE_ID]: deviceId,
          [ROUTE_SLUGS.DOC_ID]: docId,
        })
      );
    }
  };

  const handleBlockedUnapprovedVersion = () => {
    setStatusGuardModal(null);
    handleNavigateBack();
  };

  const handleCreateNewVersion = async () => {
    setStatusGuardModal(null);
    const newVersion = await createVersionMutation.mutateAsync({
      docId,
    });
    setSearchParams((params) => {
      const nextParams = new URLSearchParams(params);
      nextParams.set("v", newVersion.id);
      return nextParams;
    });
  };

  if (statusGuardModal) {
    const isLive = statusGuardModal === "LIVE";
    return (
      <>
        <MultiStepFormSkeleton />
        <ConfirmationModal
          open
          title={isLive ? "Live version" : "Version awaiting approval"}
          content={
            isLive
              ? "This is a live version. To re-answer questions, a new draft version must be created. Would you like to create a new version?"
              : "This version is awaiting approval. Use Edit Content on the document before changing answers."
          }
          confirmText={isLive ? "Create new version" : "Go back"}
          onConfirm={
            isLive ? handleCreateNewVersion : handleBlockedUnapprovedVersion
          }
          onAbort={() => {
            setStatusGuardModal(null);
            handleNavigateBack();
          }}
        />
      </>
    );
  }

  const filteredSteps = getFilteredElements(steps, device, documents);

  if (filteredSteps.length === 0) {
    return (
      <>
        <MultiStepFormSkeleton />
        <ConfirmationModal
          open
          title="No questions"
          content="There are no questions for this document version."
          confirmText="Go back"
          onConfirm={handleNavigateBack}
        />
      </>
    );
  }

  const activeStep = steps.find((s) => s.id === searchParams.get("step"));

  if (!activeStep) {
    return <MultiStepFormSkeleton />;
  }

  const isLastStep = activeStep.id === steps[steps.length - 1].id;
  const isFirstStep = activeStep.id === steps[0].id;

  const backDisabled = isFirstStep;
  const forwardDisabled =
    isLastStep ||
    !documentVersion.answers.find(
      (a: DocumentAnswer) => a.element === activeStep.id
    );

  return (
    <MultiStepFormErrorBoundary
      deviceId={deviceId}
      orgId={orgId}
      userId={user?.id}
      deviceName={device.name}
      docId={docId}
      templateKey={document.templateKey}
      docVersion={documentVersion.revision?.toString()}
      docVersionId={documentVersion.id}
      tasks={tasks}
      templatesConfig={templatesConfig}
      step={activeStep}
      stepId={activeStep.id}
      steps={filteredSteps}
    >
      <div className="flex flex-1 flex-col gap-y-2">
        <PageHeader
          pageTitleComponent={
            <PageBreadcrumb
              items={[
                { label: "Document Assistant" },
                { label: document.title },
              ]}
            />
          }
          leftButtons={
            <IconButton
              onClick={getLeftButtonProps().onClick}
              size="small"
              edge="start"
            >
              <SquareArrowLeft className="text-gray-500 size-[16px]" />
            </IconButton>
          }
          rightButtons={
            <div className="flex gap-x-1">
              <IconButton
                onClick={() => handlePrevStep(activeStep, filteredSteps)}
                disabled={backDisabled}
                size="small"
              >
                <ChevronLeft className="text-gray-500 size-[16px]" />
              </IconButton>
              <IconButton
                onClick={() => handleNextStep(activeStep, filteredSteps)}
                disabled={forwardDisabled}
                size="small"
              >
                <ChevronRight className="text-gray-500 size-[16px]" />
              </IconButton>
            </div>
          }
        />
        <div className="flex flex-1 flex-col gap-y-2 py-2 px-3">
          <div className="flex">
            <div className="flex flex-1 flex-col">
              {/* <FormPageTitle title={"Document Assistant: " + document.title} /> */}
              <ProgressBubbles
                filteredSteps={filteredSteps}
                activeStep={activeStep}
                documentVersion={documentVersion}
              />
            </div>
          </div>

          {/* Hidden is needed to make slide relative to container work https://github.com/mui/material-ui/issues/31977#issuecomment-1253487009 */}
          <div className="flex flex-1 overflow-hidden" ref={containerRef}>
            {filteredSteps.map((step) => (
              <MultiStepFormStep
                user={user}
                templatesConfig={templatesConfig}
                containerRef={containerRef}
                activeStep={activeStep}
                device={device}
                documents={documents}
                documentVersion={documentVersion}
                key={step.id}
                step={step}
                steps={filteredSteps}
                tasks={tasks}
              />
            ))}
          </div>
        </div>
        <UpdateDeviceDescriptionModal device={device} />
      </div>
    </MultiStepFormErrorBoundary>
  );
};
